import { useEffect, useRef, useState } from 'react';
import type { ConnectionWizardCommonProps } from '../connectionTypes';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';

type OAuth2WizardProps = ConnectionWizardCommonProps & {
  connected: boolean;
};

const OAuth2Wizard = ({
  restBasePath,
  nonce,
  apiBaseUrl,
  onClose,
  onConnectionUpdated,
  onSessionExpired,
  connected,
}: OAuth2WizardProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const listenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  useEffect(() => {
    const listener = listenerRef.current;
    return () => {
      if (listener) {
        window.removeEventListener('message', listener);
      }
    };
  }, []);

  const handlePrepareOAuth = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetchWithNonce(`${apiBaseUrl}${restBasePath}/prepare-oauth`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setError(await parseApiError(response));
        return;
      }

      if (!response.ok) {
        setError(await parseApiError(response));
        return;
      }

      const payload = (await response.json()) as { authorize_url?: string };
      if (typeof payload.authorize_url !== 'string' || payload.authorize_url === '') {
        setError(__('Réponse serveur invalide.'));
        return;
      }

      const popup = window.open(payload.authorize_url, `${restBasePath}_oauth`, 'width=600,height=800');
      if (!popup) {
        setError(__("La fenêtre d'authentification a été bloquée."));
        return;
      }

      const listener = (event: MessageEvent) => {
        const data = event.data ?? {};
        if (data.source !== 'accounting-hub-pennylane') {
          return;
        }

        window.removeEventListener('message', listener);
        listenerRef.current = null;

        if (data.status === 'success') {
          onConnectionUpdated();
          onClose();
          return;
        }

        setIsSubmitting(false);
        setError(__('La connexion a échoué. Réessayez.'));
      };

      listenerRef.current = listener;
      window.addEventListener('message', listener);
    } catch {
      setError(__('Impossible de démarrer la connexion.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetchWithNonce(`${apiBaseUrl}${restBasePath}/disconnect`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setError(await parseApiError(response));
        return;
      }

      if (!response.ok) {
        setError(await parseApiError(response));
        return;
      }

      onConnectionUpdated();
      onClose();
    } catch {
      setError(__('Impossible de se déconnecter.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="connection-wizard-content">
      <p>
        {connected
          ? __('Cette API est déjà connectée. Vous pouvez relancer OAuth2 ou déconnecter.')
          : __("Lancez la connexion OAuth2 pour autoriser l'accès.")}
      </p>
      <div className="connection-wizard-actions">
        <button type="button" className="btn-secondary" onClick={() => void handlePrepareOAuth()} disabled={isSubmitting}>
          {connected ? __('Reconnecter') : __('Connecter')}
        </button>
        {connected ? (
          <button type="button" className="btn-secondary" onClick={() => void handleDisconnect()} disabled={isSubmitting}>
            {__('Déconnecter')}
          </button>
        ) : null}
      </div>
      {error ? <p className="connection-wizard-error">{error}</p> : null}
    </div>
  );
};

export default OAuth2Wizard;
